import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// An electrical substation: a fenced gravel yard with two power transformers
// (tank, cooling-fin radiators, bushings on top), a steel gantry carrying a
// busbar on post insulators, and a tall lightning mast. The grid node that steps
// voltage up or down between transmission and distribution.
const STEEL = "#878e96";
const STEEL_DK = "#6c727a";
const TANK = "#8a9197";
const FIN = "#767d83";
const INSUL = "#9aa6ad";
const BUS = "#b87333";

function strut(a: Vec3, b: Vec3, thick: number, color: string): Part {
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const mid: Vec3 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
  const pitch = Math.acos(Math.max(-1, Math.min(1, dy / len)));
  const yaw = Math.atan2(dx, dz);
  return { geometry: new THREE.BoxGeometry(thick, len, thick), color, position: mid, rotation: [pitch, yaw, 0], eulerOrder: "YXZ" };
}

function transformer(cx: number, cz: number): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(0.34, 0.34, 0.3), color: TANK, position: [cx, 0.27, cz] },
    // Cooling-fin radiators on both long sides.
    { geometry: new THREE.BoxGeometry(0.05, 0.3, 0.26), color: FIN, position: [cx - 0.2, 0.27, cz] },
    { geometry: new THREE.BoxGeometry(0.05, 0.3, 0.26), color: FIN, position: [cx + 0.2, 0.27, cz] },
    // Conservator tank across the top.
    { geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.3, 10), color: TANK, position: [cx, 0.5, cz - 0.12], rotation: [0, 0, Math.PI / 2] },
  ];
  // Three HV bushings on the lid.
  for (const x of [-0.1, 0, 0.1]) {
    out.push({ geometry: new THREE.CylinderGeometry(0.025, 0.035, 0.16, 8), color: INSUL, position: [cx + x, 0.52, cz + 0.05] });
  }
  return out;
}

export const substation = defineModel({
  id: "substation",
  batchId: "electric",
  name: "Substation",
  tags: ["building", "electric", "power", "grid", "substation", "transformer"],
  summary: "A fenced yard with power transformers, a busbar gantry and a lightning mast.",
  footprint: 1.0,
  parts(): Part[] {
    const parts: Part[] = [
      { geometry: new THREE.BoxGeometry(1.9, 0.08, 1.4), color: "#7f7a70", position: [0, 0.04, 0] },
    ];
    // Perimeter fence: corner posts + a top rail along each side.
    const fx = 0.9, fz = 0.65, fy = 0.3;
    const fenceCorners: Vec3[] = [[-fx, 0, -fz], [fx, 0, -fz], [fx, 0, fz], [-fx, 0, fz]];
    for (const [x, , z] of fenceCorners) {
      parts.push({ geometry: new THREE.BoxGeometry(0.04, fy, 0.04), color: STEEL_DK, position: [x, fy / 2, z] });
    }
    for (let i = 0; i < 4; i++) {
      const a = fenceCorners[i], b = fenceCorners[(i + 1) % 4];
      parts.push(strut([a[0], fy, a[2]], [b[0], fy, b[2]], 0.02, STEEL_DK));
      parts.push(strut([a[0], fy * 0.5, a[2]], [b[0], fy * 0.5, b[2]], 0.012, "#5f656b"));
    }
    // Two transformers.
    parts.push(...transformer(-0.45, 0.1), ...transformer(0.0, 0.1));
    // Busbar gantry: two A-posts carrying a copper busbar on post insulators.
    for (const x of [0.35, 0.78]) {
      parts.push({ geometry: new THREE.BoxGeometry(0.05, 0.8, 0.05), color: STEEL, position: [x, 0.4, -0.4] });
      parts.push({ geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.12, 8), color: INSUL, position: [x, 0.86, -0.4] });
    }
    parts.push({ geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.5, 8), color: BUS, position: [0.565, 0.94, -0.4], rotation: [0, 0, Math.PI / 2] });
    // Lightning mast in the far corner.
    parts.push({ geometry: new THREE.CylinderGeometry(0.02, 0.03, 1.3, 8), color: STEEL, position: [-0.78, 0.65, -0.45] });
    parts.push({ geometry: new THREE.ConeGeometry(0.02, 0.12, 6), color: STEEL_DK, position: [-0.78, 1.36, -0.45] });
    return parts;
  },
  animations: [],
});
